import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { Option } from 'fp-ts/Option';
import { LocalizedText } from '@mews/framework';
import { SelectField } from '@mews-commander/form';
import { createOption, LocalizedTypography, OptimusProvider } from '@mews-commander/ui';
import { TextKey, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { Banner, Link, Stack } from '@optimus-web/core';
import { BillType } from '@mews-commander/data-access';
import { findCounterByBillType, useGetCreditNoteCounters } from '@mews-commander/counters/utils';

interface Props {
    billType: Option<BillType>;
    labelTextKey: Mews.TextKey;
    fieldName: string;
}

export const CreditNoteCounterSelect = ({ billType, labelTextKey, fieldName }: Props) => {
    const counters = useGetCreditNoteCounters();

    const options = pipe(
        counters,
        array.filter(findCounterByBillType(billType)),
        array.map(({ id, name }) =>
            createOption({
                value: id,
                label: name,
            })
        )
    );

    const noOptions = options.length === 0;

    return (
        <Stack vertical spacing="300">
            {noOptions ? (
                <Banner
                    primaryText={<LocalizedText textKey={TextKey.CreditNoteCounterNotCreatedTitle} />}
                    secondaryText={
                        <Stack vertical spacing="100">
                            <LocalizedTypography
                                textKey={TextKey.CreditNoteCounterNotCreatedDescription}
                                textStyle="bodyMedium"
                                as="div"
                            />
                            <Link
                                underline={false}
                                target="_blank"
                                href={createGuideLink(ArticlePath.CreditNoteCounters)}
                                rel="noreferrer"
                            >
                                <LocalizedTypography
                                    as="span"
                                    underline
                                    textStyle="bodyMediumStrong"
                                    textKey={TextKey.CreditNoteCounterCreateTitle}
                                />
                            </Link>
                        </Stack>
                    }
                    variant="warning"
                />
            ) : null}
            <OptimusProvider density="spacious">
                <SelectField
                    disabled={noOptions}
                    options={options}
                    label={<LocalizedText textKey={labelTextKey} />}
                    name={fieldName}
                    required
                />
            </OptimusProvider>
        </Stack>
    );
};
